import { useState } from 'react';
import ImageCard from '@components/common/ImageCard';
import { FiChevronsRight, FiFeather, FiBook, FiUsers } from 'react-icons/fi';
import { DATA_BASE_URL } from '@lib/constants';

const CommunityOutreachSection = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const communityPhotos = [
    {
      src: `${DATA_BASE_URL}/data/social_impact/kolkata/kolkata_chess.webp`,
      alt: 'Chess workshop with community members in Kolkata',
      category: 'Chess Education',
    },
    {
      src: `${DATA_BASE_URL}/data/social_impact/kolkata/kolkata_general.webp`,
      alt: 'Community engagement initiative in Kolkata',
      category: 'Community Service',
    },
    {
      src: `${DATA_BASE_URL}/data/social_impact/kolkata/kolkata_dog_1.webp`,
      alt: 'Animal care initiative - Supporting local dog shelter',
      category: 'Animal Welfare',
    },
    {
      src: `${DATA_BASE_URL}/data/social_impact/kolkata/kolkata_dog_2.webp`,
      alt: 'Volunteering at animal shelter in Kolkata',
      category: 'Animal Welfare',
    },
  ];

  const initiatives = [
    {
      title: 'Chess for Communities',
      description: 'Organizing free chess workshops for underprivileged communities in Kolkata and beyond, making the game accessible to all.',
      icon: FiChevronsRight,
    },
    {
      title: 'Animal Welfare',
      description: 'Promoting animal welfare by supporting street dogs in Kolkata through hands-on care, veterinary assistance, and community advocacy for humane treatment and sterilization.',
      icon: FiFeather,
    },
    {
      title: 'Educational Workshops',
      description: 'Conducting educational sessions that combine chess with life skills like strategic thinking, patience, and decision-making.',
      icon: FiBook,
    },
    {
      title: 'Community Partnerships',
      description: 'Building partnerships with local organizations to create sustainable programs that benefit communities long-term.',
      icon: FiUsers,
    },
  ];

  return (
    <section className="py-16 md:py-24">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: 'var(--color-heading)' }}
          >
            Community Outreach
          </h2>
          <p
            className="text-lg md:text-xl leading-relaxed"
            style={{ color: 'var(--color-subheading)' }}
          >
            Making a difference at the grassroots level through chess education, animal welfare, and community empowerment in Kolkata and surrounding areas.
          </p>
        </div>

        {/* Photo Gallery */}
        <div className="mb-16">
          <h3
            className="text-2xl font-semibold mb-8 text-center"
            style={{ color: 'var(--color-heading)' }}
          >
            In the Community
          </h3>
          {/* Desktop: Grid layout */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {communityPhotos.map((photo, index) => (
              <div
                key={index}
                className="cursor-pointer group"
                onClick={() => setSelectedImage(photo)}
              >
                <div className="transform transition-all duration-300 group-hover:scale-105">
                  <ImageCard
                    src={photo.src}
                    alt={photo.alt}
                    aspectRatio="aspect-square"
                    className="group-hover:shadow-xl"
                  />
                </div>
                {/* Category Badge */}
                <div
                  className="mt-3 inline-block px-3 py-1 rounded-full text-xs font-semibold"
                  style={{
                    backgroundColor: 'var(--color-accent-2)',
                    color: 'var(--color-accent-8)',
                  }}
                >
                  {photo.category}
                </div>
                <p
                  className="mt-2 text-sm"
                  style={{ color: 'var(--color-muted)' }}
                >
                  {photo.alt}
                </p>
              </div>
            ))}
          </div>
          {/* Mobile: Horizontal scroll */}
          <div className="md:hidden overflow-x-auto scrollbar-hide -mx-4 px-4">
            <div className="flex gap-4 pb-2" style={{ minWidth: 'min-content' }}>
              {communityPhotos.map((photo, index) => (
                <div
                  key={index}
                  className="cursor-pointer flex-shrink-0"
                  style={{ width: '280px' }}
                  onClick={() => setSelectedImage(photo)}
                >
                  <ImageCard
                    src={photo.src}
                    alt={photo.alt}
                    aspectRatio="aspect-square"
                    className="shadow-md"
                  />
                  {/* Category Badge */}
                  <div
                    className="mt-3 inline-block px-3 py-1 rounded-full text-xs font-semibold"
                    style={{
                      backgroundColor: 'var(--color-accent-2)',
                      color: 'var(--color-accent-8)',
                    }}
                  >
                    {photo.category}
                  </div>
                  <p
                    className="mt-2 text-sm"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {photo.alt}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Initiatives Grid */}
        <div>
          <h3
            className="text-2xl font-semibold mb-8 text-center"
            style={{ color: 'var(--color-heading)' }}
          >
            My initiatives
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {initiatives.map((initiative, index) => (
              <div
                key={index}
                className="p-6 rounded-lg transition-all duration-200"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  borderColor: 'var(--color-card-border)',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-accent-5)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-card-border)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Icon */}
                <div
                  className="mb-4 w-14 h-14 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: 'var(--color-accent-2)' }}
                >
                  {initiative.icon && <initiative.icon size={28} style={{ color: 'var(--color-accent-6)' }} />}
                </div>

                {/* Title */}
                <h4
                  className="text-xl font-semibold mb-3"
                  style={{ color: 'var(--color-heading)' }}
                >
                  {initiative.title}
                </h4>

                {/* Description */}
                <p
                  className="text-base leading-relaxed"
                  style={{ color: 'var(--color-card-text)' }}
                >
                  {initiative.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Impact Statement */}
        <div
          className="mt-16 max-w-4xl mx-auto p-8 rounded-lg text-center"
          style={{
            backgroundColor: 'var(--color-accent-1)',
            borderColor: 'var(--color-accent-3)',
            borderWidth: '1px',
            borderStyle: 'solid',
          }}
        >
          <p
            className="text-xl md:text-2xl font-medium italic leading-relaxed"
            style={{ color: 'var(--color-heading)' }}
          >
            "Every community deserves access to education, opportunity, and compassion. Through chess and service, I am building bridges that transform lives."
          </p>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-5xl max-h-screen">
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-screen object-contain rounded-lg"
            />
            <p
              className="mt-4 text-center text-lg"
              style={{ color: 'white' }}
            >
              {selectedImage.alt}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default CommunityOutreachSection;
